import { useEffect, useState } from "react";
import { GUIDES } from "./registry";
import useGuidanceProgress from "./useGuidanceProgress";

// ── Help launcher ─────────────────────────────────────────────────────────
// Always-on floating widget (bottom-left, so it doesn't collide with the
// AI assistant FAB on the bottom-right). Opens a panel listing every guide so
// users can start or replay them on demand — this is the re-entry point that
// makes skipping safe. Also lets users turn auto-guidance off entirely.

const STATUS_LABEL = {
  completed: "Completed",
  skipped: "Skipped",
  seen: "Viewed",
};

export default function HelpLauncher({ onStartGuide }) {
  const [open, setOpen] = useState(false);
  const { state, setDisabled } = useGuidanceProgress();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const launch = (id) => {
    setOpen(false);
    onStartGuide?.(id);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Help and guides"
          title="Help & guides"
          data-tour="help-launcher"
          style={fab}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </button>
      )}

      {open && (
        <>
          <div style={scrim} onClick={() => setOpen(false)} role="presentation" />
          <div role="dialog" aria-label="Help and guides" style={panel}>
            <header style={panelHeader}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
                  Help &amp; guides
                </div>
                <div style={{ fontSize: 11, color: "#94A3B8" }}>
                  Learn how to use Ample Hire — start any guide anytime.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={closeBtn}
              >
                ×
              </button>
            </header>

            <div style={list}>
              {GUIDES.map((g) => {
                const status = state.guides?.[g.id];
                const seen = !!status;
                return (
                  <div key={g.id} style={row}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>
                          {g.title}
                        </span>
                        {status && (
                          <span style={statusPill}>{STATUS_LABEL[status] || status}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                        {g.description}
                      </div>
                    </div>
                    <button type="button" onClick={() => launch(g.id)} style={startBtn}>
                      {seen ? "Replay" : "Start"}
                    </button>
                  </div>
                );
              })}
            </div>

            <footer style={panelFooter}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={!state.disabled}
                  onChange={(e) => setDisabled(!e.target.checked)}
                  style={{ accentColor: "#7B5AC4" }}
                />
                Show tips automatically
              </label>
            </footer>
          </div>
        </>
      )}
    </>
  );
}

const fab = {
  position: "fixed",
  bottom: 24,
  left: 24,
  width: 48,
  height: 48,
  borderRadius: 24,
  background: "white",
  color: "#7B5AC4",
  border: "1px solid #E2E8F0",
  cursor: "pointer",
  boxShadow: "0 8px 20px rgba(15,23,42,0.15)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 60,
};
const scrim = {
  position: "fixed",
  inset: 0,
  background: "transparent",
  zIndex: 60,
};
const panel = {
  position: "fixed",
  bottom: 24,
  left: 24,
  width: 360,
  maxWidth: "calc(100vw - 48px)",
  maxHeight: "70vh",
  background: "white",
  border: "1px solid #E2E8F0",
  borderRadius: 14,
  boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
  zIndex: 61,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
const panelHeader = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 8,
  padding: "14px 16px",
  borderBottom: "1px solid #E2E8F0",
};
const closeBtn = {
  border: "none",
  background: "transparent",
  fontSize: 22,
  lineHeight: 1,
  color: "#94A3B8",
  cursor: "pointer",
};
const list = { overflowY: "auto", padding: "6px 0" };
const row = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 16px",
};
const statusPill = {
  fontSize: 10,
  fontWeight: 600,
  color: "#64748B",
  background: "#F1F5F9",
  borderRadius: 100,
  padding: "2px 8px",
};
const startBtn = {
  flexShrink: 0,
  padding: "6px 14px",
  borderRadius: 6,
  background: "#7B5AC4",
  color: "white",
  border: "none",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
const panelFooter = {
  padding: "10px 16px",
  borderTop: "1px solid #E2E8F0",
  background: "#F8FAFC",
};
